import React, { useState, useEffect, Fragment } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import CountUp from "react-countup";
import { Users, Briefcase, MapPin, Home, Activity } from "react-feather";
import { userService } from "../../../services/userService";
import { partnerService } from "../../../services/partnerService";
import { pointService } from "../../../services/pointService";
import { siteService } from "../../../services/siteService";
import { getCurrentUserPartnerUuid, getCurrentUserType } from "../../../util/auth";

const StatCard = ({ icon: Icon, label, value, color, loading }) => (
  <Card style={{ borderTop: `3px solid ${color}` }}>
    <CardBody className="d-flex align-items-center gap-3">
      <div style={{
        width: 48, height: 48, borderRadius: 12,
        background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Icon size={22} color={color} />
      </div>
      <div>
        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#202125', lineHeight: 1 }}>
          {loading ? '—' : <CountUp end={value} duration={1.5} separator="," />}
        </div>
        <div style={{ fontSize: '0.8125rem', color: '#6B7280', marginTop: 2 }}>{label}</div>
      </div>
    </CardBody>
  </Card>
);

const Dashboard = () => {
  const userType = getCurrentUserType();
  const partnerUuid = getCurrentUserPartnerUuid();
  const isAdmin = userType === 'ADMIN';
  const [stats, setStats] = useState({ users: 0, partners: 0, points: 0, sites: 0 });
  const [loading, setLoading] = useState(true);
  const [recentUsers, setRecentUsers] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const userParams = isAdmin ? { size: 1 } : { partnerUuid, size: 1 };
        const [usersRes, pointsRes, sitesRes] = await Promise.all([
          userService.list(userParams),
          pointService.search({ size: 1 }),
          siteService.search({ size: 1 }),
        ]);

        let partnerCount = 0;
        if (isAdmin) {
          const partnersRes = await partnerService.list({ size: 1 });
          partnerCount = partnersRes?.totalElements || 0;
        }

        setStats({
          users: usersRes?.totalElements || 0,
          partners: partnerCount,
          points: pointsRes?.totalElements || 0,
          sites: sitesRes?.totalElements || 0,
        });

        const recentParams = isAdmin
          ? { size: 5, sort: '-createdDate' }
          : { partnerUuid, size: 5, sort: '-createdDate' };
        const recentRes = await userService.list(recentParams);
        setRecentUsers(recentRes?.content || []);
      } catch (e) {
        // stats stay at 0
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [isAdmin, partnerUuid]);

  const adminCards = [
    { icon: Users, label: 'Total Users', value: stats.users, color: '#00A7AA' },
    { icon: Briefcase, label: 'Partners', value: stats.partners, color: '#FF5855' },
    { icon: MapPin, label: 'Points', value: stats.points, color: '#8B5CF6' },
    { icon: Home, label: 'Sites', value: stats.sites, color: '#10B981' },
  ];

  const managerCards = [
    { icon: Users, label: 'My Users', value: stats.users, color: '#00A7AA' },
    { icon: MapPin, label: 'Points', value: stats.points, color: '#8B5CF6' },
    { icon: Home, label: 'Sites', value: stats.sites, color: '#10B981' },
  ];

  const cards = isAdmin ? adminCards : managerCards;
  const colSize = isAdmin ? 3 : 4;

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Overview" parent="Home" title="Overview" />
      <Container fluid>
        <Row className="mb-4">
          {cards.map((card) => (
            <Col key={card.label} md={colSize} sm={6} className="mb-3">
              <StatCard {...card} loading={loading} />
            </Col>
          ))}
        </Row>

        <Row>
          <Col md={12}>
            <Card>
              <CardBody>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Activity size={16} color="#00A7AA" />
                  <h6 className="mb-0" style={{ color: '#202125' }}>Recent Users</h6>
                </div>
                {loading ? (
                  <p className="text-muted">Loading...</p>
                ) : recentUsers.length === 0 ? (
                  <p className="text-muted">No users found.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover table-sm">
                      <thead>
                        <tr style={{ fontSize: '0.8125rem', color: '#6B7280' }}>
                          <th>Username</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody style={{ fontSize: '0.875rem' }}>
                        {recentUsers.map((u) => (
                          <tr key={u.id}>
                            <td>{u.userName || (u.firstName + ' ' + u.lastName)}</td>
                            <td>{u.email}</td>
                            <td>
                              <span className={`badge ${u.userType === 'ADMIN' ? 'bg-danger' : u.userType === 'MANAGER' ? 'bg-warning text-dark' : 'bg-secondary'}`} style={{ fontSize: '0.75rem' }}>
                                {u.userType}
                              </span>
                            </td>
                            <td>
                              <span style={{ color: u.active ? '#10B981' : '#6B7280', fontSize: '0.8125rem' }}>
                                {u.active ? '● Active' : '○ Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default Dashboard;
